class RecentlyBoughtPopup extends HTMLElement {
  constructor() {
    super();
    this.popup = this.querySelector('.rb-popup');
    this.closeBtn = this.querySelector('.rb-popup__close');
    this.image = this.querySelector('.rb-popup__image');
    this.productTitle = this.querySelector('.rb-popup__product-title');
    this.timeElement = this.querySelector('.rb-popup__time');
    
    this.sectionId = this.dataset.sectionId;
    this.collectionHandle = this.dataset.collection;
    this.displayTime = parseInt(this.dataset.displayTime) * 1000 || 3000;
    this.intervalTime = parseInt(this.dataset.intervalTime) * 1000 || 30000;
    this.randomMode = this.dataset.random === 'true';
    this.productsCount = parseInt(this.dataset.productsCount) || 5;
    this.showTimeAgo = this.dataset.showTimeAgo === 'true';
    this.minutesAgo = parseInt(this.dataset.minutesAgo) || 5;
    
    this.products = [];
    this.currentIndex = 0;
    this.intervalId = null;
    this.timeoutId = null;
    this.isVisible = false;
    
    // Style variables
    this.popup.style.setProperty('--background-color', this.getStyleColor('background_color', '#FFFFFF'));
    this.popup.style.setProperty('--text-color', this.getStyleColor('text_color', '#000000'));
    this.popup.style.setProperty('--accent-color', this.getStyleColor('accent_color', '#666666'));
    this.popup.style.setProperty('--border-radius', this.getStyleNumber('border_radius', '8') + 'px');
    this.popup.style.setProperty('--spacing', this.getStyleNumber('padding', '20') + 'px');
    
    this.init();
  }

  getStyleColor(setting, defaultValue) {
    // Try to get from section settings
    const section = document.querySelector(`.section-${this.sectionId}`);
    if (section) {
      const style = getComputedStyle(section);
      const color = style.getPropertyValue(`--${setting}`);
      if (color) return color;
    }
    return defaultValue;
  }

  getStyleNumber(setting, defaultValue) {
    const section = document.querySelector(`.section-${this.sectionId}`);
    if (section) {
      const style = getComputedStyle(section);
      const value = style.getPropertyValue(`--${setting}`);
      if (value) return parseInt(value);
    }
    return parseInt(defaultValue);
  }

  async init() {
    if (!this.collectionHandle) {
      console.warn('No collection selected for recently bought popup');
      return;
    }

    await this.fetchProducts();
    
    if (this.products.length === 0) {
      console.warn('No products found in selected collection');
      return;
    }

    this.setupEventListeners();
    this.startPopupCycle();
  }

  async fetchProducts() {
    try {
      const url = `/collections/${this.collectionHandle}/products.json?limit=${this.productsCount}`;
      const response = await fetch(url);
      const data = await response.json();
      
      this.products = data.products.map(product => ({
        id: product.id,
        title: product.title,
        handle: product.handle,
        image: product.images?.[0]?.src || null,
        url: `/products/${product.handle}`
      }));

      // Shuffle if random mode
      if (this.randomMode) {
        this.products = this.shuffleArray(this.products);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  }

  shuffleArray(array) {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  }

  setupEventListeners() {
    // Close button
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.hidePopup();
      });
    }

    // Click on product title
    if (this.productTitle) {
      this.productTitle.addEventListener('click', (e) => {
        e.preventDefault();
        const product = this.products[this.currentIndex];
        if (product && product.url) {
          window.location.href = product.url;
        }
      });
    }
  }

  startPopupCycle() {
    // Initial popup after 2 seconds
    setTimeout(() => {
      this.showNextPopup();
    }, 2000);

    // Set interval for subsequent popups
    this.intervalId = setInterval(() => {
      this.showNextPopup();
    }, this.intervalTime);
  }

  showNextPopup() {
    if (this.products.length === 0) return;

    // Get next product
    if (this.randomMode) {
      this.currentIndex = Math.floor(Math.random() * this.products.length);
    } else {
      this.currentIndex = (this.currentIndex + 1) % this.products.length;
    }

    const product = this.products[this.currentIndex];
    this.updatePopupContent(product);
    this.showPopup();
  }

  updatePopupContent(product) {
    // Update image
    if (this.image && product.image) {
      this.image.src = product.image;
      this.image.alt = product.title;
    }

    // Update title and link
    if (this.productTitle) {
      this.productTitle.textContent = this.truncateTitle(product.title, 40);
      this.productTitle.href = product.url;
    }

    // Update time ago
    if (this.timeElement && this.showTimeAgo) {
      const minutes = this.getRandomMinutes();
      this.timeElement.textContent = `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    }
  }

  truncateTitle(title, maxLength = 40) {
    if (title.length <= maxLength) return title;
    return title.substring(0, maxLength - 3) + '...';
  }

  getRandomMinutes() {
    // Random minutes between 1 and set minutes
    const min = 1;
    const max = this.minutesAgo;
    const minutes = Math.floor(Math.random() * (max - min + 1)) + min;
    return minutes;
  }

  showPopup() {
    if (this.isVisible) {
      this.hidePopup(() => {
        setTimeout(() => {
          this.popup.classList.add('active');
          this.popup.classList.remove('hiding');
          this.isVisible = true;
        }, 300);
      });
    } else {
      this.popup.classList.add('active');
      this.popup.classList.remove('hiding');
      this.isVisible = true;
    }

    // Auto hide after display time
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    this.timeoutId = setTimeout(() => {
      this.hidePopup();
    }, this.displayTime);
  }

  hidePopup(callback) {
    this.popup.classList.remove('active');
    this.popup.classList.add('hiding');
    this.isVisible = false;
    
    // Ensure pointer-events are disabled immediately
    this.popup.style.pointerEvents = 'none';
    
    if (callback) {
      setTimeout(callback, 300);
    }
    
    // Remove the inline style after animation completes
    setTimeout(() => {
      if (!this.isVisible) {
        this.popup.style.pointerEvents = '';
      }
    }, 350);
  }

  disconnectedCallback() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  }
}

customElements.define('recently-bought-popup', RecentlyBoughtPopup);